import { useState, useCallback, useRef, useEffect } from 'react';

// ダイアログの状態を管理する型
type DialogConfig = {
  title: string;
  message: string;
  onConfirm: () => void;
  defaultFocus?: 'ok' | 'cancel';
};

/**
 * アプリ共通の確認ダイアログと背景グレー化を制御するカスタムフック
 */
export const useAppDialog2 = () => {
  // 表示するダイアログの情報（null なら非表示）
  const [dialogConfig, setDialogConfig] = useState<DialogConfig | null>(null);

  const okButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // ダイアログが開いた(dialogConfigに値が入った)瞬間に、OKボタンにフォーカスを当てる
  useEffect(() => {
    if (dialogConfig) {
      // 画面のレンダリングが完了してから確実にフォーカスを当てるため、わずかな遅延(10ms)を入れるのがReactのコツです
      setTimeout(() => {
        // 指定された方にフォーカスを当てる
        if (dialogConfig.defaultFocus === 'cancel') {
          cancelButtonRef.current?.focus();
        } else {
          okButtonRef.current?.focus();
        }
      }, 10);
    }
  }, [dialogConfig]);

  /**
   * 確認ダイアログを表示する
   */
  const showConfirm = useCallback(({ title, message, onConfirm, defaultFocus = 'ok' }: DialogConfig) => {
    setDialogConfig({ title, message, onConfirm, defaultFocus });
  }, []);

  /**
   * ダイアログを閉じる
   */
  const closeDialog = useCallback(() => {
    setDialogConfig(null);
  }, []);

  /**
   * 背景グレーとダイアログ本体をまとめた要素
   * 呼び出し元で {OverlayElement} としてレンダリングされる
   */
  const OverlayElement = dialogConfig ? (
    // 画面全体を覆うコンテナ（flexで中身を絶対に上下左右中央に配置する）
    <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-auto">
      
      {/* 背景のグレーアウト（クリックでキャンセル扱い） */}
      <div 
        className="absolute inset-0 bg-black/40 transition-opacity duration-300" 
        onClick={closeDialog} 
      />

      {/* ダイアログ本体（relativeで背景より手前に配置） */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-5 md:p-6 w-[calc(100vw-2rem)] md:w-[500px] flex flex-col border border-slate-200 animate-in fade-in zoom-in-95 duration-200">
        
        {/* 左上のタイトル的文言 */}
        <div className="text-[12px] font-bold text-slate-500 mb-3 leading-none">
          {dialogConfig.title}
        </div>
        
        {/* メインメッセージ：whitespace-pre-wrapで改行を有効化 */}
        <div className="text-[16px] md:text-[18px] font-bold text-slate-800 leading-relaxed whitespace-pre-wrap mb-6">
          {dialogConfig.message}
        </div>
        
        {/* ボタンエリア */}
        <div className="flex justify-end gap-3 w-full mt-auto">
          {/* キャンセルボタン：フォーカス時に太いグレーの枠線が出るように修正 */}
          <button
            ref={cancelButtonRef}
            className="bg-white hover:bg-slate-50 text-slate-700 px-5 py-2 min-w-[110px] rounded-lg font-bold text-[15px] border border-slate-300 transition-all
                       focus:ring-4 focus:ring-slate-200 focus:border-slate-400 focus:outline-none"
            onClick={closeDialog}
          >
            キャンセル
          </button>
          
          {/* OKボタン：フォーカス時に太いインディゴの枠線が出るように修正 */}
          <button
            ref={okButtonRef}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 min-w-[110px] rounded-lg font-bold text-[15px] shadow-sm transition-all
                       focus:ring-4 focus:ring-indigo-200 focus:outline-none"
            onClick={() => {
              dialogConfig.onConfirm();
              closeDialog();
            }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // setIsOverlayActive はもう使わないので、互換性のためにダミー関数を返しておきます
  return { 
    showConfirm, 
    OverlayElement, 
    setIsOverlayActive: () => {},
    isOpen: dialogConfig !== null
  };
};